import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { patient } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';
import type { LabOrder } from '@/types/medical';

export function PatientLabOrdersPage() {
  const q = useApiQuery({
    queryKey: ['patient', 'lab-orders'],
    queryFn:  patient.labOrders.list,
  });

  return (
    <>
      <PageHeader title="My lab orders" />
      <DataState
        query={q}
        empty={{
          title: 'No lab orders yet',
          description: 'When your clinician orders lab tests, they appear here. Bring this list (or your phone) to the lab.',
        }}
      >
        {(items) => (
          <Stack spacing={1.5}>
            {items.map((o) => <Row key={o.id} order={o} />)}
          </Stack>
        )}
      </DataState>
    </>
  );
}

function Row({ order }: { order: LabOrder }) {
  const colorMap: Record<LabOrder['status'], 'default' | 'primary' | 'success' | 'warning'> = {
    draft: 'default', issued: 'primary',
    collected: 'primary', in_progress: 'primary',
    reported: 'success', cancelled: 'default',
  };
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'background.subtle', color: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ScienceIcon />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {order.panel_name || 'Lab panel'}
              </Typography>
              <Chip
                size="small"
                label={order.status.replace(/_/g, ' ')}
                color={colorMap[order.status]}
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {order.test_codes?.length ?? 0} test(s)
              {order.indication_icd_code && ` · ICD ${order.indication_icd_code}`}
            </Typography>
            {order.instructions && (
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.5 }}>
                {order.instructions}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {order.issued_at && (
              <Typography variant="caption" color="text.secondary" className="tabular">
                issued {formatDateTime(order.issued_at)}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
